/** Month display names — same order as JS Date month index 0–11. */
export const REPORTS_MONTH_ORDER = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * @param {string} isoMonth — e.g. `2026-04`
 * @returns {string} e.g. `April 2026`
 */
export function formatIsoMonth(isoMonth) {
  if (!isoMonth) return "";
  const [year, month] = String(isoMonth).split("-");
  const name = REPORTS_MONTH_ORDER[parseInt(month, 10) - 1];
  return name && year ? `${name} ${year}` : isoMonth;
}

/**
 * @param {string} monthKey — e.g. `January|2025`
 * @returns {string | null} API month `YYYY-MM`
 */
export function displayMonthKeyToIsoMonth(monthKey) {
  const [monthName, year] = String(monthKey).split("|");
  const idx = REPORTS_MONTH_ORDER.indexOf(monthName);
  if (idx === -1 || !year) return null;
  return `${year}-${String(idx + 1).padStart(2, "0")}`;
}

/**
 * Rolling past months only (legacy / tests).
 * @returns {Array<{ value: string, label: string }>} value like `January|2025`
 */
export function generateRollingMonthDropdownOptions(monthsBack = 24) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < monthsBack; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = REPORTS_MONTH_ORDER[d.getMonth()];
    const year = d.getFullYear();
    out.push({ value: `${monthName}|${year}`, label: `${monthName} ${year}` });
  }
  return out;
}

/**
 * Month options for Lock Month form: **pastMonths** back through **futureMonths** ahead of today’s month (calendar months).
 * Default: previous 12 months + current month + next 5 months (18 options).
 * @param {number} [pastMonths=12]
 * @param {number} [futureMonths=5]
 * @returns {Array<{ value: string, label: string }>} value like `January|2025`
 */
export function generateLockMonthWindowOptions(pastMonths = 12, futureMonths = 5) {
  const out = [];
  const now = new Date();
  const anchor = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = -pastMonths; i <= futureMonths; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() + i, 1);
    const monthName = REPORTS_MONTH_ORDER[d.getMonth()];
    const year = d.getFullYear();
    out.push({ value: `${monthName}|${year}`, label: `${monthName} ${year}` });
  }
  return out;
}

/** Current calendar month as dropdown value `January|2026`. */
export function getCurrentMonthDisplayKey() {
  const now = new Date();
  const monthName = REPORTS_MONTH_ORDER[now.getMonth()];
  const year = now.getFullYear();
  return `${monthName}|${year}`;
}

/**
 * Sort `{ value, label }` options by calendar order (`January|2025` …), not label A–Z.
 * @param {Array<{ value: string, label: string }>} opts
 */
export function sortMonthYearDropdownOptions(opts) {
  return [...opts].sort((a, b) => compareMonthYearValueKeys(a.value, b.value));
}

/** Compare two `MonthName|YYYY` keys chronologically. */
export function compareMonthYearValueKeys(aVal, bVal) {
  const [ma, ya] = String(aVal).split("|");
  const [mb, yb] = String(bVal).split("|");
  const dy = Number(ya) - Number(yb);
  if (dy !== 0) return dy;
  return REPORTS_MONTH_ORDER.indexOf(ma) - REPORTS_MONTH_ORDER.indexOf(mb);
}

/**
 * Merge POST /month-locks response into MonthLockingSection row shape.
 * @param {Record<string, unknown>} api — created resource (or nested `data`)
 * @param {{ month: string, clientLabel: string }} meta — request context
 */
export function monthLockRowFromCreateResponse(api, meta) {
  if (!meta?.month) return null;
  const ym = api?.month != null ? String(api.month) : meta.month;
  const parts = String(ym).split("-");
  const yStr = parts[0];
  const mStr = parts[1];
  if (!yStr || !mStr) return null;
  const mi = parseInt(mStr, 10) - 1;
  const monthName = REPORTS_MONTH_ORDER[mi];
  if (!monthName) return null;
  const year = parseInt(yStr, 10);
  const stRaw = String(api?.status ?? "unlocked").toLowerCase();
  const status = stRaw === "locked" ? "Locked" : "Unlocked";
  return {
    id: api?.id != null ? String(api.id) : `${ym}-${meta.clientLabel}`,
    month: monthName,
    year,
    client: meta.clientLabel,
    status,
    lockedBy: api?.lockedBy ?? "—",
    lockedOn: api?.lockedOn ?? null,
  };
}

function formatLockStatus(raw) {
  const stRaw = String(raw ?? "unlocked").toLowerCase();
  return stRaw === "locked" ? "Locked" : "Unlocked";
}

/**
 * Map GET /month-locks list item to MonthLockingSection row shape.
 * Supports API rows (`month: "YYYY-MM"`) and UI-shaped mocks (`month: "January"`, `year: 2025`).
 *
 * @param {Record<string, unknown>} api
 * @returns {object | null}
 */
export function monthLockRowFromListItem(api) {
  if (!api || typeof api !== "object") return null;

  if (
    typeof api.month === "string" &&
    REPORTS_MONTH_ORDER.includes(api.month) &&
    (typeof api.year === "number" || (typeof api.year === "string" && api.year !== ""))
  ) {
    const y = typeof api.year === "number" ? api.year : parseInt(String(api.year), 10);
    if (!Number.isFinite(y)) return null;
    return {
      id: api.id != null ? String(api.id) : `${api.month}|${y}|${api.client ?? "Global"}`,
      month: api.month,
      year: y,
      client: typeof api.client === "string" && api.client.trim() ? api.client : "Global",
      status: formatLockStatus(api.status),
      lockedBy: api.lockedBy ?? "—",
      lockedOn: api.lockedOn ?? null,
    };
  }

  const ym = api.month != null ? String(api.month) : "";
  const parts = ym.split("-");
  const yStr = parts[0];
  const mStr = parts[1];
  if (!yStr || !mStr) return null;
  const mi = parseInt(mStr, 10) - 1;
  const monthName = REPORTS_MONTH_ORDER[mi];
  if (!monthName) return null;
  const year = parseInt(yStr, 10);

  const client =
    (typeof api.clientName === "string" && api.clientName.trim()) ||
    (typeof api.client === "string" && api.client.trim()) ||
    (api.client && typeof api.client === "object"
      ? api.client.clientName || api.client.name
      : null) ||
    (api.clientId == null || api.clientId === "" ? "Global" : "—");

  return {
    id: api.id != null ? String(api.id) : `${ym}-${client}`,
    month: monthName,
    year,
    client,
    status: formatLockStatus(api.status),
    lockedBy: api.lockedBy ?? api.lockedByName ?? "—",
    lockedOn: api.lockedOn ?? api.lockedOnDate ?? null,
  };
}
